import { geoMercator, geoPath } from "d3-geo"
import type { GeoProjection } from "d3-geo"
import type { Feature, Polygon } from "geojson"
import {
  Check,
  LocateFixed,
  Minus,
  PencilLine,
  Plus,
  RotateCcw,
  ScanSearch,
  Trash2,
  X,
} from "lucide-react"
import { useEffect, useId, useMemo, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import type { Scene } from "@/lib/domain"
import {
  createDrawnAreaFeature,
  DRAWN_AREA_PREVIEW_PATH,
} from "@/lib/drawn-area"
import type { DrawnAreaCoordinate, DrawnAreaFeature } from "@/lib/drawn-area"
import { cn } from "@/lib/utils"

const MAP_WIDTH = 1000
const MAP_HEIGHT = 620
const OSM_TILE_ZOOM = 6

type MapTile = {
  key: string
  href: string
  x: number
  y: number
  width: number
  height: number
}

type ProjectedBounds = {
  x: number
  y: number
  width: number
  height: number
}

const areaFeature: Feature<Polygon> = {
  type: "Feature",
  properties: {},
  geometry: {
    type: "Polygon",
    coordinates: [
      [
        [123.48, 13.37],
        [123.97, 13.35],
        [123.99, 12.93],
        [123.52, 12.92],
        [123.48, 13.37],
      ],
    ],
  },
}

function sceneFeature(scene: Scene): Feature<Polygon> {
  return {
    type: "Feature",
    properties: { id: scene.id },
    geometry: {
      type: "Polygon",
      coordinates: [[...scene.footprint, scene.footprint[0]]],
    },
  }
}

function createProjection(zoom: number): GeoProjection {
  return geoMercator()
    .center([122.1, 12.8])
    .scale(1_420 * zoom)
    .translate([500, 310])
}

function longitudeToTileX(longitude: number, zoom: number) {
  return ((longitude + 180) / 360) * 2 ** zoom
}

function latitudeToTileY(latitude: number, zoom: number) {
  const latitudeRadians = (latitude * Math.PI) / 180
  return ((1 - Math.asinh(Math.tan(latitudeRadians)) / Math.PI) / 2) * 2 ** zoom
}

function tileXToLongitude(tileX: number, zoom: number) {
  return (tileX / 2 ** zoom) * 360 - 180
}

function tileYToLatitude(tileY: number, zoom: number) {
  const mercator = Math.PI * (1 - (2 * tileY) / 2 ** zoom)
  return (Math.atan(Math.sinh(mercator)) * 180) / Math.PI
}

function createMapTiles(projection: GeoProjection): Array<MapTile> {
  const northWest = projection.invert?.([0, 0])
  const southEast = projection.invert?.([MAP_WIDTH, MAP_HEIGHT])
  if (!northWest || !southEast) return []

  const tileCount = 2 ** OSM_TILE_ZOOM
  const startX = Math.floor(longitudeToTileX(northWest[0], OSM_TILE_ZOOM))
  const endX = Math.ceil(longitudeToTileX(southEast[0], OSM_TILE_ZOOM))
  const startY = Math.max(
    0,
    Math.floor(latitudeToTileY(northWest[1], OSM_TILE_ZOOM))
  )
  const endY = Math.min(
    tileCount - 1,
    Math.ceil(latitudeToTileY(southEast[1], OSM_TILE_ZOOM))
  )
  const tiles: Array<MapTile> = []

  for (let rawX = startX; rawX <= endX; rawX += 1) {
    const tileX = ((rawX % tileCount) + tileCount) % tileCount
    for (let tileY = startY; tileY <= endY; tileY += 1) {
      const northWestPoint = projection([
        tileXToLongitude(rawX, OSM_TILE_ZOOM),
        tileYToLatitude(tileY, OSM_TILE_ZOOM),
      ])
      const southEastPoint = projection([
        tileXToLongitude(rawX + 1, OSM_TILE_ZOOM),
        tileYToLatitude(tileY + 1, OSM_TILE_ZOOM),
      ])
      if (!northWestPoint || !southEastPoint) continue

      tiles.push({
        key: `${OSM_TILE_ZOOM}-${rawX}-${tileY}`,
        href: `https://tile.openstreetmap.org/${OSM_TILE_ZOOM}/${tileX}/${tileY}.png`,
        x: northWestPoint[0],
        y: northWestPoint[1],
        width: southEastPoint[0] - northWestPoint[0],
        height: southEastPoint[1] - northWestPoint[1],
      })
    }
  }

  return tiles
}

function projectedBounds(
  coordinates: ReadonlyArray<readonly [number, number]>,
  projection: GeoProjection
): ProjectedBounds | null {
  const points = coordinates.flatMap((coordinate) => {
    const point = projection([coordinate[0], coordinate[1]])
    return point ? [point] : []
  })
  if (points.length === 0) return null

  const xValues = points.map(([x]) => x)
  const yValues = points.map(([, y]) => y)
  const x = Math.min(...xValues)
  const y = Math.min(...yValues)

  return {
    x,
    y,
    width: Math.max(...xValues) - x,
    height: Math.max(...yValues) - y,
  }
}

export function PhilippinesMap({
  scenes,
  selectedSceneId,
  onSelectScene,
  className,
  areaLabel = "Legazpi–Daraga corridor · 146.2 km²",
  showArea = true,
  drawingEnabled = false,
  drawnArea = null,
  onDrawnAreaChange,
  viewResetKey = 0,
}: {
  scenes: Array<Scene>
  selectedSceneId?: string | null
  onSelectScene?: (sceneId: string) => void
  className?: string
  areaLabel?: string
  showArea?: boolean
  drawingEnabled?: boolean
  drawnArea?: DrawnAreaFeature | null
  onDrawnAreaChange?: (feature: DrawnAreaFeature | null) => void
  viewResetKey?: number
}) {
  const [zoom, setZoom] = useState(1)
  const [drawing, setDrawing] = useState(false)
  const [draftCoordinates, setDraftCoordinates] = useState<
    Array<DrawnAreaCoordinate>
  >([])
  const [cursorCoordinate, setCursorCoordinate] = useState<DrawnAreaCoordinate>(
    [122.1, 12.8]
  )
  const [drawStatus, setDrawStatus] = useState(
    "Select Draw polygon to define a custom area."
  )
  const svgRef = useRef<SVGSVGElement>(null)
  const previousViewResetKey = useRef(viewResetKey)
  const instanceId = useId().replaceAll(":", "")
  const imageryClipId = `drawn-imagery-clip-${instanceId}`
  const selectedImageryClipId = `selected-imagery-clip-${instanceId}`
  const drawInstructionsId = `draw-instructions-${instanceId}`
  const mapTitleId = `service-map-title-${instanceId}`
  const mapDescriptionId = `service-map-description-${instanceId}`
  const { projection, path, mapTiles } = useMemo(() => {
    const nextProjection = createProjection(zoom)
    return {
      projection: nextProjection,
      path: geoPath(nextProjection),
      mapTiles: createMapTiles(nextProjection),
    }
  }, [zoom])
  const selectedScene = scenes.find((scene) => scene.id === selectedSceneId)
  const selectedScenePath = selectedScene
    ? (path(sceneFeature(selectedScene)) ?? undefined)
    : undefined
  const selectedSceneBounds = selectedScene
    ? projectedBounds(selectedScene.footprint, projection)
    : null
  const activeArea: Feature<Polygon> = drawnArea
    ? {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          // d3-geo uses clockwise exterior rings for small spherical polygons,
          // while the exported RFC 7946 GeoJSON remains counterclockwise.
          coordinates: [[...drawnArea.geometry.coordinates[0]].reverse()],
        },
      }
    : areaFeature
  const activeAreaPath = path(activeArea) ?? undefined
  const drawnAreaBounds = drawnArea
    ? projectedBounds(
        drawnArea.geometry.coordinates[0] as Array<[number, number]>,
        projection
      )
    : null
  const draftPoints = draftCoordinates.flatMap((coordinate) => {
    const point = projection(coordinate)
    return point ? [point] : []
  })
  const cursorPoint = projection(cursorCoordinate)

  useEffect(() => {
    if (previousViewResetKey.current === viewResetKey) return

    previousViewResetKey.current = viewResetKey
    setZoom(1)
    setDrawStatus("Map view centered on the active area of interest.")
  }, [viewResetKey])

  function focusMap() {
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => svgRef.current?.focus())
    } else {
      window.setTimeout(() => svgRef.current?.focus(), 0)
    }
  }

  function beginDrawing() {
    setDraftCoordinates([])
    setDrawing(true)
    setDrawStatus(
      "Drawing started. Click the map to add vertices, or use arrow keys and Space."
    )
    focusMap()
  }

  function cancelDrawing() {
    setDraftCoordinates([])
    setDrawing(false)
    setDrawStatus("Drawing cancelled. The previous area was kept.")
  }

  function addCoordinate(coordinate: DrawnAreaCoordinate) {
    const previous = draftCoordinates.at(-1)
    if (
      previous &&
      previous[0] === coordinate[0] &&
      previous[1] === coordinate[1]
    ) {
      return
    }

    const nextCoordinates = [...draftCoordinates, coordinate]
    setDraftCoordinates(nextCoordinates)
    setDrawStatus(
      `Vertex ${nextCoordinates.length} added.${
        nextCoordinates.length >= 3
          ? " Select Finish polygon or press Enter."
          : " Add at least three vertices."
      }`
    )
  }

  function undoVertex() {
    if (draftCoordinates.length === 0) {
      setDrawStatus("There are no vertices to undo.")
      return
    }

    const nextCoordinates = draftCoordinates.slice(0, -1)
    setDraftCoordinates(nextCoordinates)
    setDrawStatus(
      nextCoordinates.length === 0
        ? "All vertices removed."
        : `Vertex removed. ${nextCoordinates.length} remaining.`
    )
    focusMap()
  }

  function finishDrawing() {
    if (draftCoordinates.length < 3) {
      setDrawStatus("Add at least three vertices before finishing the polygon.")
      focusMap()
      return
    }

    try {
      const completedFeature = createDrawnAreaFeature(draftCoordinates, {
        createdAt: new Date().toISOString(),
      })
      onDrawnAreaChange?.(completedFeature)
      setDraftCoordinates([])
      setDrawing(false)
      setDrawStatus(
        `Polygon complete. The area boundary and KML export are ready for ${completedFeature.properties.areaSquareKilometers.toLocaleString(
          "en-US",
          { maximumFractionDigits: 2 }
        )} square kilometers.`
      )
    } catch (error) {
      setDrawStatus(
        error instanceof Error
          ? error.message
          : "The polygon could not be completed. Adjust its vertices and try again."
      )
      focusMap()
    }
  }

  function mapPointFromPointer(
    event: React.PointerEvent<SVGSVGElement> | React.MouseEvent<SVGSVGElement>
  ): [number, number] | null {
    const matrix = event.currentTarget.getScreenCTM()
    if (matrix && typeof DOMPoint !== "undefined") {
      const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(
        matrix.inverse()
      )
      return [point.x, point.y]
    }

    const bounds = event.currentTarget.getBoundingClientRect()
    if (bounds.width === 0 || bounds.height === 0) return null
    return [
      ((event.clientX - bounds.left) / bounds.width) * 1000,
      ((event.clientY - bounds.top) / bounds.height) * 620,
    ]
  }

  function coordinateFromMapPoint(point: [number, number]) {
    const coordinate = projection.invert?.(point)
    return coordinate
      ? ([coordinate[0], coordinate[1]] as DrawnAreaCoordinate)
      : null
  }

  function handleMapClick(event: React.MouseEvent<SVGSVGElement>) {
    if (!drawing) return
    const point = mapPointFromPointer(event)
    const coordinate = point ? coordinateFromMapPoint(point) : null
    if (coordinate) addCoordinate(coordinate)
  }

  function handlePointerMove(event: React.PointerEvent<SVGSVGElement>) {
    if (!drawing) return
    const point = mapPointFromPointer(event)
    const coordinate = point ? coordinateFromMapPoint(point) : null
    if (coordinate) setCursorCoordinate(coordinate)
  }

  function handleMapKeyDown(event: React.KeyboardEvent<SVGSVGElement>) {
    if (!drawing) return

    if (
      event.key === "ArrowUp" ||
      event.key === "ArrowDown" ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight"
    ) {
      event.preventDefault()
      const currentPoint = projection(cursorCoordinate) ?? [500, 310]
      const step = event.shiftKey ? 24 : 8
      const nextPoint: [number, number] = [
        Math.min(
          992,
          Math.max(
            8,
            currentPoint[0] +
              (event.key === "ArrowLeft"
                ? -step
                : event.key === "ArrowRight"
                  ? step
                  : 0)
          )
        ),
        Math.min(
          612,
          Math.max(
            8,
            currentPoint[1] +
              (event.key === "ArrowUp"
                ? -step
                : event.key === "ArrowDown"
                  ? step
                  : 0)
          )
        ),
      ]
      const coordinate = coordinateFromMapPoint(nextPoint)
      if (coordinate) setCursorCoordinate(coordinate)
      return
    }

    if (event.key === " ") {
      event.preventDefault()
      addCoordinate(cursorCoordinate)
    } else if (event.key === "Enter") {
      event.preventDefault()
      finishDrawing()
    } else if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault()
      undoVertex()
    } else if (event.key === "Escape") {
      event.preventDefault()
      cancelDrawing()
    }
  }

  return (
    <section
      className={cn(
        "service-map relative isolate min-h-[430px] overflow-hidden rounded-[14px] bg-[#d9edf1] ring-1 ring-[#b7cdd2]",
        className
      )}
      aria-label="Imagery coverage map of the Philippines"
    >
      <svg
        ref={svgRef}
        viewBox="0 0 1000 620"
        role="group"
        aria-labelledby={mapTitleId}
        aria-describedby={`${mapDescriptionId}${
          drawing ? ` ${drawInstructionsId}` : ""
        }`}
        tabIndex={drawing ? 0 : undefined}
        className={cn(
          "absolute inset-0 size-full outline-none",
          drawing &&
            "cursor-crosshair focus-visible:ring-4 focus-visible:ring-[#087c9e] focus-visible:ring-inset"
        )}
        preserveAspectRatio="xMidYMid slice"
        onClick={handleMapClick}
        onPointerMove={handlePointerMove}
        onKeyDown={handleMapKeyDown}
      >
        <title id={mapTitleId}>
          {drawing
            ? "Polygon drawing surface"
            : "Philippine imagery coverage map"}
        </title>
        <desc id={mapDescriptionId}>
          An OpenStreetMap basemap showing an area of interest, catalogue scene
          footprints, and an optional user-drawn polygon across the Philippines.
          Scene records and imagery previews are demonstration data.
        </desc>

        <defs>
          <clipPath id={imageryClipId}>
            <path d={drawnArea ? activeAreaPath : undefined} />
          </clipPath>
          <clipPath id={selectedImageryClipId}>
            <path d={selectedScenePath} />
          </clipPath>
        </defs>

        <rect width={MAP_WIDTH} height={MAP_HEIGHT} fill="#dbe7e4" />
        <g aria-hidden="true" pointerEvents="none">
          {mapTiles.map((tile) => (
            <image
              key={tile.key}
              className="map-tile"
              href={tile.href}
              x={tile.x}
              y={tile.y}
              width={tile.width + 0.5}
              height={tile.height + 0.5}
              preserveAspectRatio="none"
            />
          ))}
          <rect
            width={MAP_WIDTH}
            height={MAP_HEIGHT}
            fill="#f3f8f7"
            opacity="0.08"
          />
        </g>

        {selectedScene && selectedSceneBounds && selectedScenePath ? (
          <image
            href={selectedScene.preview}
            x={selectedSceneBounds.x}
            y={selectedSceneBounds.y}
            width={selectedSceneBounds.width}
            height={selectedSceneBounds.height}
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#${selectedImageryClipId})`}
            opacity="0.9"
            pointerEvents="none"
          />
        ) : null}

        {showArea ? (
          <>
            {drawnArea && drawnAreaBounds ? (
              <image
                href={DRAWN_AREA_PREVIEW_PATH}
                x={drawnAreaBounds.x}
                y={drawnAreaBounds.y}
                width={drawnAreaBounds.width}
                height={drawnAreaBounds.height}
                preserveAspectRatio="xMidYMid slice"
                clipPath={`url(#${imageryClipId})`}
                opacity="0.9"
                pointerEvents="none"
              />
            ) : (
              <path
                d={activeAreaPath}
                fill="#22b6cf"
                fillOpacity="0.18"
                stroke="#087c9e"
                strokeWidth="2.4"
                strokeDasharray="7 5"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            )}
            <path
              d={activeAreaPath}
              fill="none"
              stroke="#ffffff"
              strokeOpacity="0.9"
              strokeWidth={drawnArea ? 6 : 5}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
            <path
              d={activeAreaPath}
              fill="none"
              stroke={drawnArea ? "#075e79" : "#087c9e"}
              strokeWidth={drawnArea ? 3.2 : 2.4}
              strokeDasharray={drawnArea ? undefined : "7 5"}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          </>
        ) : null}

        {scenes.map((scene) => {
          const selected = scene.id === selectedSceneId
          const marker = projection(scene.center)
          const footprintPath = path(sceneFeature(scene))

          if (!marker) {
            return null
          }

          return (
            <g key={scene.id}>
              <path
                d={footprintPath ?? undefined}
                fill={selected ? "#0b6fa4" : "#22b6cf"}
                fillOpacity={selected ? 0.28 : 0.12}
                stroke={selected ? "#0b5d8a" : "#1499b1"}
                strokeWidth={selected ? 3 : 1.6}
                vectorEffect="non-scaling-stroke"
                className={cn(
                  "focus:fill-opacity-35 transition-[fill-opacity,stroke,stroke-width] duration-300 outline-none focus-visible:stroke-[#f2bd3d] focus-visible:[stroke-width:4]",
                  drawing ? "pointer-events-none" : "cursor-pointer"
                )}
                role="button"
                tabIndex={drawing ? -1 : 0}
                aria-pressed={selected}
                aria-label={`Select ${scene.satellite} scene over ${scene.location}`}
                onClick={(event) => {
                  event.stopPropagation()
                  onSelectScene?.(scene.id)
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    onSelectScene?.(scene.id)
                  }
                }}
              />
              <g
                transform={`translate(${marker[0]} ${marker[1]})`}
                className="pointer-events-none"
              >
                <circle
                  r={selected ? 10 : 7}
                  fill={selected ? "#123a5a" : "#ffffff"}
                  stroke={selected ? "#ffffff" : "#0b6fa4"}
                  strokeWidth="3"
                  vectorEffect="non-scaling-stroke"
                />
                <circle r="2.4" fill={selected ? "#22b6cf" : "#0b6fa4"} />
              </g>
            </g>
          )
        })}

        {drawing && draftPoints.length > 0 ? (
          <g>
            <polyline
              points={[...draftPoints, ...(cursorPoint ? [cursorPoint] : [])]
                .map((point) => point.join(","))
                .join(" ")}
              fill={draftPoints.length >= 3 ? "rgb(34 182 207 / 18%)" : "none"}
              stroke="#075e79"
              strokeWidth="3"
              strokeDasharray="8 5"
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
            {draftPoints.map((point, index) => (
              <g key={`${point[0]}-${point[1]}-${index}`}>
                {index === 0 && draftPoints.length >= 3 ? (
                  <circle
                    cx={point[0]}
                    cy={point[1]}
                    r="14"
                    fill="transparent"
                    stroke="transparent"
                    className="cursor-pointer"
                    role="button"
                    tabIndex={-1}
                    aria-label="Finish polygon at the first vertex"
                    onClick={(event) => {
                      event.stopPropagation()
                      finishDrawing()
                    }}
                  />
                ) : null}
                <circle
                  cx={point[0]}
                  cy={point[1]}
                  r={index === 0 ? 6 : 5}
                  fill={index === 0 ? "#f2bd3d" : "#ffffff"}
                  stroke="#075e79"
                  strokeWidth="3"
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                />
              </g>
            ))}
          </g>
        ) : null}

        {drawing && cursorPoint ? (
          <g
            transform={`translate(${cursorPoint[0]} ${cursorPoint[1]})`}
            pointerEvents="none"
          >
            <circle
              r="9"
              fill="rgb(255 255 255 / 82%)"
              stroke="#075e79"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d="M-13 0h26M0-13v26"
              stroke="#075e79"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ) : null}
      </svg>

      <div className="service-map-label absolute top-3 left-3 z-10 rounded-[8px] bg-[#123a5a]/94 px-3 py-2 text-white shadow-[0_8px_22px_-14px_rgba(18,58,90,.8)]">
        <p className="flex items-center gap-1.5 text-[0.67rem] leading-none font-semibold">
          <ScanSearch className="size-3.5 text-[var(--cyan)]" />
          Synthetic coverage map
        </p>
        <a
          href="https://www.openstreetmap.org/copyright"
          className="mt-1 block text-[0.6rem] text-white/82 underline decoration-white/40 underline-offset-2 hover:text-white focus-visible:rounded-[2px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          © OpenStreetMap contributors
        </a>
      </div>

      {drawingEnabled ? (
        <div
          className="map-drawing-toolbar absolute top-[76px] left-3 z-20 max-w-[calc(100%-5.5rem)] rounded-[12px] border border-[#adc8ce] bg-white/96 p-2 shadow-[0_12px_30px_-18px_rgba(18,58,90,.72)]"
          role="toolbar"
          aria-label="Polygon drawing controls"
        >
          <div className="flex flex-wrap gap-1.5">
            {drawing ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  onClick={finishDrawing}
                  disabled={draftCoordinates.length < 3}
                >
                  <Check />
                  Finish polygon
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={undoVertex}
                  disabled={draftCoordinates.length === 0}
                >
                  <RotateCcw />
                  Undo vertex
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={cancelDrawing}
                >
                  <X />
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button type="button" size="sm" onClick={beginDrawing}>
                  <PencilLine />
                  {drawnArea ? "Redraw polygon" : "Draw polygon"}
                </Button>
                {drawnArea ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onDrawnAreaChange?.(null)
                      setDrawStatus(
                        "Custom polygon cleared. The preset area is active."
                      )
                    }}
                  >
                    <Trash2 />
                    Clear
                  </Button>
                ) : null}
              </>
            )}
          </div>
          <p
            id={drawInstructionsId}
            className="mt-2 max-w-[50ch] px-1 text-[0.66rem] leading-4 font-medium text-[#455e69]"
          >
            {drawing
              ? "Click to add vertices. Keyboard: arrows move, Space adds, Enter finishes, Backspace undoes, Esc cancels."
              : drawnArea
                ? "AOI boundary ready. Redraw to replace this polygon."
                : "Create an AOI polygon with pointer or keyboard controls."}
          </p>
        </div>
      ) : null}

      <div className="map-control-stack absolute top-3 right-3 z-10 flex flex-col gap-1 rounded-[10px] bg-white p-1 shadow-[0_10px_28px_-16px_rgba(18,58,90,.55)]">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="instrument-control"
          onClick={() =>
            setZoom((value) => Math.min(1.6, Number((value + 0.2).toFixed(1))))
          }
          disabled={zoom >= 1.6}
          aria-label="Zoom map in"
          title="Zoom in"
        >
          <Plus />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="instrument-control"
          onClick={() =>
            setZoom((value) => Math.max(0.8, Number((value - 0.2).toFixed(1))))
          }
          disabled={zoom <= 0.8}
          aria-label="Zoom map out"
          title="Zoom out"
        >
          <Minus />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="instrument-control"
          onClick={() => setZoom(1)}
          aria-label="Reset map view"
          title="Reset view"
        >
          <LocateFixed />
        </Button>
      </div>

      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {drawStatus}
      </p>

      <div className="map-footer-overlays absolute inset-x-3 bottom-3 z-10 flex flex-wrap items-end justify-between gap-3">
        {showArea ? (
          <div className="map-overlay-panel max-w-[360px] rounded-[10px] border border-[#c2d1d5] bg-white/96 px-3 py-2 shadow-[0_10px_28px_-18px_rgba(18,58,90,.6)]">
            <p className="text-[0.62rem] font-semibold text-[#526872]">
              Area of interest
            </p>
            <p className="mt-0.5 text-xs font-semibold text-[var(--navy)]">
              {areaLabel}
            </p>
          </div>
        ) : (
          <span />
        )}
        <div className="map-overlay-panel flex items-center gap-3 rounded-[10px] border border-[#c2d1d5] bg-white/96 px-3 py-2 text-[0.62rem] font-semibold text-[var(--ink)] shadow-[0_10px_28px_-18px_rgba(18,58,90,.6)]">
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-[2px] border border-[#087c9e] bg-[#22b6cf]/25" />
            {drawnArea ? "Drawn area" : "Search area"}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-full border-2 border-[#0b6fa4] bg-white" />
            Scene
          </span>
        </div>
      </div>
    </section>
  )
}
